import { Controller } from 'stimulus';
import Cookies from 'js-cookie';
import events from 'lib/events';

const gaEventsListOnce = [
  events.download_btn.initial_click,
  events.download_btn.match_eezycore_free_dupe,
  events.errors.error404_page,
  events.locale_prompt.locale_prompt_accepted,
  events.locale_prompt.locale_prompt_connects,
  events.locale_prompt.locale_prompt_dismissed,
  events.locale_prompt.switch_language,
  events.search.search_type,
  events.search.without_results,
  events.search.trending_search_click,
  events.sessions.new_account_created,
  events.show_page.attribution_join_pro_click,
  events.show_page.download_ad_modal_connects,
  events.show_page.download_limit_modal_connects,
  events.show_page.license_selector_free_modal_closes,
  events.show_page.license_selector_free_modal_connects,
  events.show_page.license_selector_free_modal_free_button,
  events.show_page.license_selector_free_modal_learn_more,
  events.show_page.license_selector_free_modal_pro_button,
  events.site_header.explore_brusheezy,
  events.site_header.explore_nav,
  events.site_header.explore_newest,
  events.site_header.explore_cats,
  events.site_header.explore_videezy,
  events.site_header.header_join_pro,
  events.site_header.header_login,
  events.site_header.header_signup,
  events.sponsored_search.position,
  events.sponsored_search.without_results,
  events.checkout.error,
  events.partner.pagination_popup,
  events.partner.search_popup,
];

const gaEventsListAlways = [
  events.search.filter,
  events.search.hide_show_search_filters,
  events.show_page.attribution_link_copied,
  // start split-test: resource_show_modal_view
  events.show_page.modal.prev_arrow_click,
  events.show_page.modal.next_arrow_click,
  events.show_page.modal.close_click,
  // end split-test: resource_show_modal_view
  events.site_header.related_tag_click,
  events.partner.results_click,
  events.search.header_search_filter,
];

/* Split Test GA Events */
const gaEventsSplitTestOnce = [];
const gaEventsSplitTestAlways = [];

export default class GoogleAnalyticsController extends Controller {
  connect() {
    this.sentPageView = false;

    window.dataLayer = window.dataLayer || [];
    this.loadGA(window, document, 'script', this.scriptUrl, 'ga');
    this.ga('create', this.trackingId, this.site);

    this.ga((tracker) => {
      Cookies.set('ga-client-id', tracker.get('clientId'));
    });

    if (document.body.getAttribute('data-env') === 'development') {
      this.ga('set', 'checkStorageTask', null); // Don't abort tracking if cookies disabled -- for testing
    }

    this.ga('require', 'displayfeatures');
    this.ga('set', 'anonymizeIp', true);
    this.ga('set', 'dimension4', this.userDimension);
    this.ga('set', 'dimension6', 'apollo');
    this.ga('set', 'dimension7', this.userDimension7);
    this.ga('set', 'transport', 'beacon');

    // Set Google Optimize experiment ID
    if (this.optimizeExperimentID && this.optimizeVariantID) {
      this.ga('require', this.optimizeContainerID);
      this.ga('set', 'exp', `${this.optimizeExperimentID}.${this.optimizeVariantID}`);
    }

    this.sendPageView();

    window.addEventListener(events.analytics.fire_page_view, this.sendPageView);

    this.removeListeners(gaEventsListOnce, { once: true });
    this.removeListeners(gaEventsListAlways, { once: false });
    this.registerListeners(gaEventsListOnce, { once: true });
    this.registerListeners(gaEventsListAlways, { once: false });

    this.registerListeners(gaEventsSplitTestOnce, { once: true });
    this.registerListeners(gaEventsSplitTestAlways, { once: false });
  }

  disconnect() {
    window.removeEventListener(events.analytics.fire_page_view, this.sendPageView);

    this.removeListeners(gaEventsListOnce, { once: true });
    this.removeListeners(gaEventsListAlways, { once: false });

    this.removeListeners(gaEventsSplitTestOnce, { once: true });
    this.removeListeners(gaEventsSplitTestAlways, { once: false });
  }

  sendPageView = () => {
    // split-test resource_show_modal_view
    // remove extra check for modal view if split test fails
    if ((!this.sentPageView || document.querySelector('#resource-show-modal-view'))
        && !this.isTurbolinksPreview) {
      this.sentPageView = true;
      this.trackPageView(window.location.href);
    }
  }

  registerListeners(eventsList, options) {
    window.addEventListener(events.generic.track_generic_event, this.trackEvent, { once: false });

    eventsList.forEach((eventName) => {
      window.addEventListener(eventName, this.transformEvent, options);
    });
  }

  removeListeners(eventsList, options) {
    window.removeEventListener(events.generic.track_generic_event, this.trackEvent, { once: false });

    eventsList.forEach((eventName) => {
      window.removeEventListener(eventName, this.transformEvent, options);
    });
  }

  transformEvent = (event) => {
    // Clone the event so we don't modify the original and possibly affect other components listening for the event
    const transformedEvent = { ...event };
    transformedEvent.detail = { ...event.detail };

    switch (event.type) {
      case events.download_btn.initial_click:
        transformedEvent.detail.gaEventCategory = 'download';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsProToPremium(event.detail.resourceType);
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        transformedEvent.detail.gaNonInteraction = false;
        break;

      case events.download_btn.match_eezycore_free_dupe:
        transformedEvent.detail.gaEventCategory = 'download';
        transformedEvent.detail.gaEventAction = 'free';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        transformedEvent.detail.gaNonInteraction = false;
        break;

      case events.errors.error404_page:
        transformedEvent.detail.gaEventCategory = '404_page';
        transformedEvent.detail.gaEventAction = window.location.pathname;
        break;

      case events.locale_prompt.locale_prompt_accepted:
        transformedEvent.detail.gaEventCategory = 'locale_prompt';
        transformedEvent.detail.gaEventAction = `${event.detail.currentLocale}_visit_recommend_${event.detail.recommendedLocale}`;
        transformedEvent.detail.gaEventLabel = `visit_in_${event.detail.recommendedLocale}`;
        break;

      case events.locale_prompt.locale_prompt_connects:
        transformedEvent.detail.gaEventCategory = 'locale_prompt';
        transformedEvent.detail.gaEventAction = `${event.detail.currentLocale}_visit_recommend_${event.detail.recommendedLocale}`;
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.locale_prompt.locale_prompt_dismissed:
        transformedEvent.detail.gaEventCategory = 'locale_prompt';
        transformedEvent.detail.gaEventAction = `${event.detail.currentLocale}_visit_recommend_${event.detail.recommendedLocale}`;
        transformedEvent.detail.gaEventLabel = 'no_thanks_stay_here';
        break;

      case events.locale_prompt.switch_language:
        transformedEvent.detail.gaEventCategory = 'locale_prompt';
        transformedEvent.detail.gaEventAction = `${event.detail.currentLocale}_visit_recommend_${event.detail.recommendedLocale}`;
        transformedEvent.detail.gaEventLabel = `view_diff_lang_${event.detail.toLang}`;
        break;

      case events.search.filter:
        transformedEvent.detail.gaEventCategory = 'search_filters';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsSearchFilterAction(event.detail.filter);
        transformedEvent.detail.gaEventLabel = event.detail.searchTerm;
        transformedEvent.detail.gaEventValue = event.detail.filterValue;
        break;

      case events.search.hide_show_search_filters:
        transformedEvent.detail.gaEventCategory = 'search_filters';
        transformedEvent.detail.gaEventAction = event.detail.action;
        transformedEvent.detail.gaEventLabel = event.detail.searchTerm;
        break;

      case events.search.search_type:
        transformedEvent.detail.gaEventCategory = 'Search';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsSearchType(event.detail.searchType);
        transformedEvent.detail.gaEventLabel = event.detail.searchTerm;
        break;

      case events.search.without_results:
        transformedEvent.detail.gaEventCategory = 'search_without_results';
        transformedEvent.detail.gaEventAction = event.detail.searchTerm;
        break;

      case events.search.trending_search_click:
        transformedEvent.detail.gaEventCategory = 'search_trending_search_click';
        transformedEvent.detail.gaEventAction = event.detail.searchTerm;
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.sessions.new_account_created:
        transformedEvent.detail.gaEventCategory = 'account';
        transformedEvent.detail.gaEventAction = 'new account created';
        transformedEvent.detail.gaEventLabel = this.transformAnalyticsProToPremium(event.detail.accountType);
        break;

      case events.show_page.attribution_link_copied:
        transformedEvent.detail.gaEventCategory = 'attribution';
        transformedEvent.detail.gaEventAction = 'copy_attribution';
        break;

      case events.show_page.attribution_join_pro_click:
        transformedEvent.detail.gaEventCategory = 'attribution';
        transformedEvent.detail.gaEventAction = 'join pro';
        break;

      case events.show_page.download_ad_modal_connects:
        transformedEvent.detail.gaEventCategory = 'Show_640x480_DnldPopup';
        transformedEvent.detail.gaEventAction = 'Show_640x480_DnldPopup';
        break;

      case events.show_page.download_limit_modal_connects:
        transformedEvent.detail.gaEventCategory = 'pop_up triggered';
        transformedEvent.detail.gaEventAction = '3 downloads';
        break;

      case events.show_page.license_selector_free_modal_closes:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = 'user_close';
        break;

      case events.show_page.license_selector_free_modal_connects:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = 'pop_up';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.license_selector_free_modal_free_button:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsLicenseType(event.detail.licenseType);
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.license_selector_free_modal_learn_more:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = 'premium_license_learn_more';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.license_selector_free_modal_pro_button:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsLicenseType(event.detail.licenseType);
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      // start split-test: resource_show_modal_view
      case events.show_page.modal.prev_arrow_click:
        transformedEvent.detail.gaEventCategory = 'showpage_modal';
        transformedEvent.detail.gaEventAction = 'left_arrow';
        break;

      case events.show_page.modal.next_arrow_click:
        transformedEvent.detail.gaEventCategory = 'showpage_modal';
        transformedEvent.detail.gaEventAction = 'right_arrow';
        break;

      case events.show_page.modal.close_click:
        transformedEvent.detail.gaEventCategory = 'showpage_modal';
        transformedEvent.detail.gaEventAction = 'x_out';
        break;
        // end split-test: resource_show_modal_view

      case events.site_header.explore_brusheezy:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'explore_click';
        transformedEvent.detail.gaEventLabel = 'brusheezy';
        break;

      case events.site_header.explore_nav:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'explore_click';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.site_header.explore_newest:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'explore_click';
        transformedEvent.detail.gaEventLabel = 'browse_newest';
        break;

      case events.site_header.explore_cats:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'explore_click';
        transformedEvent.detail.gaEventLabel = 'browse_by_category';
        break;

      case events.site_header.explore_videezy:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'explore_click';
        transformedEvent.detail.gaEventLabel = 'videezy';
        break;

      case events.site_header.header_join_pro:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'go_premium';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.site_header.header_login:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'login';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.site_header.header_signup:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'join_eezy';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.site_header.related_tag_click:
        transformedEvent.detail.gaEventCategory = 'tag_click';
        transformedEvent.detail.gaEventAction = event.detail.tagClicked;
        transformedEvent.detail.gaEventLabel = event.detail.tagSearched;
        break;

      case events.sponsored_search.without_results:
        transformedEvent.detail.gaEventCategory = 'sponsored_search_without_results';
        transformedEvent.detail.gaEventAction = event.detail.searchTerm;
        break;

      case events.partner.pagination_popup:
        transformedEvent.detail.gaEventCategory = 'sstk_popup';
        transformedEvent.detail.gaEventAction = 'pagination';
        break;

      case events.partner.results_click:
        transformedEvent.detail.gaEventCategory = 'out_shutterstock';
        transformedEvent.detail.gaEventAction = 'click';
        break;

      case events.partner.search_popup:
        transformedEvent.detail.gaEventCategory = 'sstk_popup';
        transformedEvent.detail.gaEventAction = event.detail.subId1;
        break;

      case events.checkout.error:
        transformedEvent.detail.gaEventCategory = 'ecommerce';
        transformedEvent.detail.gaEventAction = 'error';
        transformedEvent.detail.gaEventValue = event.detail.message;
        break;

      case events.search.header_search_filter:
        transformedEvent.detail.gaEventCategory = 'header_search_filter';
        transformedEvent.detail.gaEventAction = event.detail.filterValue;
        transformedEvent.detail.gaEventLabel = event.detail.searchTerm;
        break;

      default:
        /* Do nothing */
    }

    /* Split test analytics */
    switch (event.type) {
      case events.generic.ez_toggle_change:
        if (event.detail.changedTo === 'monthly') transformedEvent.detail.gaEventLabel = 'monthly';
        else if (event.detail.changedTo === 'annual') transformedEvent.detail.gaEventLabel = 'yearly';
        else return false;

        transformedEvent.detail.gaEventCategory = 'pricing_page';
        transformedEvent.detail.gaEventAction = 'monthly_yearly_slider';
        break;

      default:
        break;
    }

    this.trackEvent(transformedEvent);
    return true;
  }

  trackPageView = (url) => {
    const pageViewUrl = this.resourceType === 'pro' ? `${url}/premium` : url;
    this.ga('set', 'location', url);
    this.ga('send', 'pageview', this.pathFromUrl(pageViewUrl));
  }

  trackEvent = (event) => {
    const fieldsObject = {
      hitType: 'event',
      nonInteraction: true,
    };

    /* refactor with Object.assign */
    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaEventCategory')) {
      fieldsObject.eventCategory = event.detail.gaEventCategory;
    }

    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaEventAction')) {
      fieldsObject.eventAction = event.detail.gaEventAction;
    }

    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaEventLabel')) {
      fieldsObject.eventLabel = event.detail.gaEventLabel;
    }

    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaEventValue')) {
      fieldsObject.eventValue = event.detail.gaEventValue;
    }

    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaNonInteraction')) {
      fieldsObject.nonInteraction = event.detail.gaNonInteraction;
    }

    this.ga('send', fieldsObject);
  }

  /* eslint-disable */
  // See https://developers.google.com/analytics/devguides/collection/analyticsjs/ for "inspiration"
  loadGA = function (i, s, o, g, r, a, m) {
    i['GoogleAnalyticsObject'] = r;
    i[r] = i[r] || function () {
      (i[r].q = i[r].q || []).push(arguments);
    }, i[r].l = 1 * new Date();
    a = s.createElement(o),
    m = s.getElementsByTagName(o)[0];
    a.async = 1;
    a.src = g;
    m.parentNode.insertBefore(a, m);
  };
  /* eslint-enable */

  ga(...args) {
    if (args[0] === 'send' && args[1] === 'event') {
      args.push({ nonInteraction: true });
    }

    window.ga(...args);
  }

  get scriptUrl() {
    return 'https://www.google-analytics.com/analytics.js';
  }

  get site() {
    return this.element.getAttribute('data-site');
  }

  get trackingId() {
    return this.element.getAttribute('data-tracking-id');
  }

  get userDimension() {
    return this.element.getAttribute('data-user-dimension');
  }

  get userDimension7() {
    return this.element.getAttribute('data-user-dimension-7');
  }

  get optimizeContainerID() {
    return this.element.getAttribute('data-optimize-container');
  }

  get optimizeExperimentID() {
    return this.element.getAttribute('data-optimize-experiment');
  }

  get optimizeVariantID() {
    return this.element.getAttribute('data-optimize-variant');
  }

  transformAnalyticsProToPremium(eventProp) {
    if (eventProp === 'pro') {
      return 'premium';
    }

    return eventProp;
  }

  transformAnalyticsLicenseType(eventProp) {
    if (eventProp === 'pro_license') {
      return 'premium_license';
    }

    return eventProp;
  }

  transformAnalyticsSearchType(type) {
    const types = {
      internal: 'Internal',
      external: 'External',
    };

    if (types[type]) {
      return types[type];
    }

    console.error('unknown searchType: %s', type); /* eslint-disable-line  no-console */
    return null;
  }

  transformAnalyticsSearchFilterAction(filter) {
    const actions = {
      editor: 'filter_by_editor_compatible',
      'license-editorial': 'filter_by_license_type_editorial_use_only',
      'license-free': 'filter_by_license_type_standard',
      'license-pro': 'filter_by_license_type_premium',
      'orientation-horizontal': 'filter_by_orientation_horizontal',
      'orientation-square': 'filter_by_orientation_square',
      'orientation-vertical': 'filter_by_orientation_vertical',
      'sort-best': 'sort_by_best_match',
      'sort-newest': 'sort_by_newest',
    };

    return actions[filter];
  }

  pathFromUrl(pageUrl) {
    const url = new URL(pageUrl);

    // split-test resource_show_modal_view
    // remove extra check for modal view if split test fails
    if (this.searchForm && !document.querySelector('#resource-show-modal-view')) {
      url.searchParams.set('site-search', this.searchTerm);
      url.searchParams.set('search-scope', this.transformAnalyticsSearchType(this.searchType));
    }

    return `${url.pathname}${url.search}`;
  }

  get searchForm() {
    const form = document.querySelector('[data-controller~="search-form"]');
    if (form && form.getAttribute('data-search-term')) {
      return form;
    }

    return null;
  }

  get searchPage() {
    const currentPageElement = document.querySelector('[data-target="ez-num-pager.currentPage"]');
    let currentPage;
    if (currentPageElement) {
      currentPage = parseInt(currentPageElement.innerHTML, 10);
    }

    if (Number.isNaN(currentPage)) {
      return 1;
    }

    return currentPage;
  }

  get searchTerm() {
    if (this.searchForm) {
      return this.searchForm.getAttribute('data-search-term');
    }
    return null;
  }

  get searchType() {
    if (this.searchForm) {
      return this.searchForm.getAttribute('data-search-type');
    }
    return null;
  }

  get isTurbolinksPreview() {
    return document.documentElement.hasAttribute('data-turbolinks-preview');
  }

  get resourceType() {
    return this.element.getAttribute('data-resource-type');
  }
}
